import axios from 'axios';
import { FirebaseProvider } from '../../infrastructure/providers/FirebaseProvider';
import { FirestoreShipmentRepository } from '@/infrastructure/repositories/FirestoreShipmentRepository';
import { Shipment } from '@/domain/entities';
import { assertCanCreateShipments } from '@/utils/companyPlan';

interface TrackingRequest {
  shipmentNumber: string | null;
  carrier: string | null;
  type: string;
  companyId: string;
  uid: string;
}

interface TrackingResponse {
  success: boolean;
  data: {
    shipmentId: string;
    notFound?: boolean;
    message?: string;
  };
  error?: {
    code: string;
    message: string;
    details?: string;
  }
}

export interface TrackingApiResponse {
  success: boolean;
  data?: {
    shipmentId?: string;
    docId?: string;
    isCreation?: boolean;
    sinayError?: boolean;
    notFound?: boolean;
    message?: string;
    details?: any[];
    containerResults?: Array<{
      container: string;
      success: boolean;
      docId?: string;
      error?: string;
    }>;
    containerBookingResults?: Array<{
      bookingNumber: string;
      success: boolean;
      docId?: string;
      error?: string;
    }>;
  };
  error?: string;
  message?: string;
  sinayError?: boolean;
  nonRecoverable?: boolean;
  originalError?: string;
  details?: any[];
}

export class TrackingService {
  constructor(private shipmentRepository: FirestoreShipmentRepository) {}

  async getTracking(request: Omit<TrackingRequest, 'companyId' | 'uid'>, companyId: string, uid: string, assignedTo?: string | null): Promise<TrackingResponse> {    
    // La CF crea el embarque: el cupo de Free Premium se valida antes de llamarla
    await assertCanCreateShipments(FirebaseProvider.getInstance().getFirestore(), companyId);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_CF_BASE_URL}/fetchAndStoreTracking`,
        {
          ...request,
          companyId,
          uid,
          assignedTo
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching tracking:', error);
      throw error;
    }
  }

  async startTracking(
    companyId: string,
    shipmentNumber: string | string[],
    carrier: string | null,
    type: string,
    uid: string,
    shipmentId: string,
    containerBookings?: Array<{ containerId: string; bookingNumber: string }>
  ): Promise<TrackingApiResponse> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000);

    try {
      const payload: Record<string, any> = { shipmentNumber, carrier, type, companyId, uid, shipmentId }
      if (containerBookings && containerBookings.length > 0) {
        payload.containerBookings = containerBookings
      }

      const response = await axios.post(
        `${import.meta.env.VITE_CF_BASE_URL}/fetchAndStoreTracking`,
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal
        }
      );

      clearTimeout(timeout);

      const apiResponse: TrackingApiResponse = response.data;

      if (apiResponse.success) {
        const updates: Partial<Shipment> = {
          isTracking: true,
          trackingStartedAt: new Date(),
          trackingStatus: 'active',
          lastTrackingUpdate: new Date(),
          updatedAt: new Date(),
          updatedBy: {
            id: 'SYSTEM',
            name: 'Tracking',
            lastName: 'Integration'
          }
        };

        await this.shipmentRepository.updateShipment(shipmentId, updates, companyId);
      }

      return apiResponse;
    } catch (error: any) {
      clearTimeout(timeout);
      console.error('Error starting tracking:', error);

      if (error.name === 'AbortError') {
        return { success: false, error: 'TIMEOUT_ERROR' };
      }

      if (axios.isAxiosError(error) && error.response?.data) {
        if (typeof error.response.data === 'object') {
          return error.response.data as TrackingApiResponse;
        }
        return { success: false, error: String(error.response.data) };
      }

      return { success: false, error: error.message || 'UNKNOWN_ERROR' };
    }
  }  
}
